import { publicIpv4 as getPublicIp } from 'public-ip';

import getConfig from '@/appconfig/configUtils';
import { fetchAPI } from '@/services/fetchAPI';

type CarrierResponse = {
  redirectUrl?: string;
};

// Local storage key for smartId caching
const SmartIdLocalstorageKey = 'SMART_ID';

let inFlightSmartIdRequest: Promise<string | null> | null = null;
let smartIdFailedThisSession = false;

const hasApiCredentials = () => {
  return (
    !!process.env.NEXT_PUBLIC_SMARTID_API_KEY &&
    !!process.env.NEXT_PUBLIC_SMARTID_API_SECRET
  );
};

const readCachedSmartId = (): string | null => {
  if (typeof window === 'undefined') return null;

  const cached = localStorage.getItem(SmartIdLocalstorageKey);
  return cached && cached.trim().length > 0 ? cached : null;
};

const markFailedThisSession = () => {
  smartIdFailedThisSession = true;
};

const isFailedThisSession = () => {
  if (smartIdFailedThisSession) return true;
  return false;
};

// Fetches carrier redirect URL for smartId
const getCarrier = async (): Promise<CarrierResponse | null> => {
  if (
    !process.env.NEXT_PUBLIC_SMARTID_API_KEY ||
    !process.env.NEXT_PUBLIC_SMARTID_API_SECRET
  ) {
    console.warn('Please include the SmartID API Key and Secret');
    return null;
  }

  try {
    const publicIp = await getPublicIp();

    const url = `https://api.smart-id.ca/sip/api/keyauth/carrierIdFinderUrl?publisher_id=${process.env.NEXT_PUBLIC_SMARTID_PUBLISHER_ID}&public_ip=${publicIp}`;

    const response = await fetchAPI(url, {
      method: 'GET',
      mode: 'cors',
      headers: {
        'X-API-Key': process.env.NEXT_PUBLIC_SMARTID_API_KEY || '',
        'X-API-SECRET': process.env.NEXT_PUBLIC_SMARTID_API_SECRET || '',
      },
      referrerPolicy: 'no-referrer',
    });

    if (response.status === 200) {
      const data = (await response.json()) as CarrierResponse;
      return data;
    } else {
      console.error(`Failed to get carrier, status: ${response.status}`);
      return null;
    }
  } catch (error) {
    console.error(`Failed to get carrier: ${error}`);
    return null;
  }
};

// Fetches smartId from network (one-time per session)
const getNetworkId = async (): Promise<string | null> => {
  if (
    !process.env.NEXT_PUBLIC_SMARTID_API_KEY ||
    !process.env.NEXT_PUBLIC_SMARTID_API_SECRET
  ) {
    console.warn('Please include the SmartID API Key and Secret');
    return null;
  }

  try {
    const carrier = await getCarrier();
    const redirectUrl = carrier?.redirectUrl;

    if (!redirectUrl) {
      return null;
    }

    const response = await fetchAPI(`${redirectUrl}&opt_in=true`, {
      method: 'GET',
      mode: 'cors',
      headers: {
        'X-API-Key': process.env.NEXT_PUBLIC_SMARTID_API_KEY || '',
        'X-API-SECRET': process.env.NEXT_PUBLIC_SMARTID_API_SECRET || '',
      },
      referrerPolicy: 'no-referrer',
    });

    if (response.status === 200) {
      const data = (await response.json()) as { smart_id?: string };
      return data.smart_id ?? null;
    } else {
      console.error(`Failed to get Network Id`);
      return null;
    }
  } catch (error) {
    console.error(`Failed to get Network Id`);
    return null;
  }
};

// Returns cached smartId if available (no fetch)
export const getCachedSmartID = (): string | null => {
  return readCachedSmartId();
};

// Resets smartId session state (for testing/retry)
export const resetSmartIdSessionState = () => {
  inFlightSmartIdRequest = null;
  smartIdFailedThisSession = false;
};

// Main smartId fetch: returns cached, dedupes, or fetches from network
export const getSmartID = async (): Promise<string | null> => {
  const config = await getConfig();

  if (!config?.featureFlags?.smartId) {
    return null;
  }

  if (!hasApiCredentials()) {
    console.warn('Please include the SmartID API Key and Secret');
    return null;
  }

  const cachedSmartId = readCachedSmartId();
  if (cachedSmartId) {
    return cachedSmartId;
  }

  if (isFailedThisSession()) {
    return null;
  }

  if (inFlightSmartIdRequest) {
    return inFlightSmartIdRequest;
  }

  inFlightSmartIdRequest = (async () => {
    const smartID = await getNetworkId();

    if (smartID) {
      if (typeof window !== 'undefined') {
        localStorage.setItem(SmartIdLocalstorageKey, smartID);
      }
      return smartID;
    }

    markFailedThisSession();
    return null;
  })();

  const result = await inFlightSmartIdRequest;
  inFlightSmartIdRequest = null;

  return result;
};
